import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import api from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';
import {
  CheckIcon,
  ChevronDownIcon,
  CountChip,
  PrinterIcon,
  SearchIcon,
  Select,
} from '../../components/ui';
import { useCrewLookups } from './useData';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import {
  rankRequirementsByNationalityPrintPath,
  rankRequirementsPrintPath,
} from './reports/rankRequirementsPrintMeta';
import type { CertCatalogLookup, LookupItem } from './types';

type RankRequirement = {
  id: number;
  rankId: number;
  vesselId?: number | null;
  certCatalogId: number;
  nationalityId: number;
  isMandatory: boolean;
  notes?: string | null;
  sortOrder: number;
  isActive: boolean;
  rank?: { id: number; name: string; abrv?: string | null } | null;
  vessel?: { id: number; vesselName: string } | null;
  certCatalog?: { id: number; name: string } | null;
  nationality?: { id: number; name: string; code?: string | null } | null;
};

type NatSettingResponse = { key: string; value: number[] };

type CertCategoryGroup = {
  key: string;
  label: string;
  sortOrder: number;
  certs: CertCatalogLookup[];
};

const CERT_COL_PX = 280;
const NAT_COL_PX = 88;

const RankRequirementsPage: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { showError, showSuccess } = useNotification();
  const { lookups } = useCrewLookups();

  const ranks = lookups?.ranks ?? [];
  const vessels = lookups?.vessels ?? [];
  const certCatalogs = lookups?.certCatalogs ?? [];
  const certCategories = lookups?.certCategories ?? [];
  const allNationalities = lookups?.nationalities ?? [];

  const { data: natSetting, isLoading: loadingNats } = useSWR<NatSettingResponse>(
    '/settings/crew/requirement-nationalities',
    fetcher,
    { revalidateOnFocus: true },
  );

  const [rankFilter, setRankFilter] = useState('');
  const [selectedRankId, setSelectedRankId] = useState<number | null>(null);
  const [vesselId, setVesselId] = useState('');
  const [appliedVesselId, setAppliedVesselId] = useState('');
  const [pendingCells, setPendingCells] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [printMenuOpen, setPrintMenuOpen] = useState(false);
  const printMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!printMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!printMenuRef.current?.contains(e.target as Node)) setPrintMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [printMenuOpen]);

  const nationalityIds = useMemo(
    () => (Array.isArray(natSetting?.value) ? natSetting!.value : []),
    [natSetting],
  );

  const matrixNationalities = useMemo(() => {
    const byId = new Map(allNationalities.map((n) => [n.id, n]));
    return nationalityIds
      .map((id) => byId.get(id))
      .filter((n): n is LookupItem => Boolean(n));
  }, [nationalityIds, allNationalities]);

  const listUrl =
    selectedRankId != null && appliedVesselId
      ? `/crew/rank-requirements?rankId=${selectedRankId}&vesselId=${encodeURIComponent(appliedVesselId)}&exactVessel=1`
      : null;

  const { data, isLoading, mutate } = useSWR<RankRequirement[]>(listUrl, fetcher, {
    revalidateOnFocus: true,
  });

  const rows = useMemo(() => data ?? [], [data]);

  const enabledSet = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      set.add(`${r.certCatalogId}:${r.nationalityId}`);
    }
    return set;
  }, [rows]);

  const filteredRanks = useMemo(() => {
    const q = rankFilter.trim().toLowerCase();
    const list = q
      ? ranks.filter(
          (r) =>
            r.name.toLowerCase().includes(q) ||
            (r.abrv ?? '').toLowerCase().includes(q) ||
            (r.code ?? '').toLowerCase().includes(q),
        )
      : ranks;
    return [...list].sort((a, b) => {
      const ao = Number(a.sortOrder);
      const bo = Number(b.sortOrder);
      const aOrder = Number.isFinite(ao) ? ao : 999_999;
      const bOrder = Number.isFinite(bo) ? bo : 999_999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      const byAbrv = (a.abrv || '').localeCompare(b.abrv || '', undefined, { sensitivity: 'base' });
      if (byAbrv !== 0) return byAbrv;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [ranks, rankFilter]);

  useEffect(() => {
    if (selectedRankId != null) return;
    if (filteredRanks.length === 0) return;
    setSelectedRankId(filteredRanks[0].id);
  }, [filteredRanks, selectedRankId]);

  const selectedRank = useMemo(
    () => ranks.find((r) => r.id === selectedRankId) ?? null,
    [ranks, selectedRankId],
  );

  const appliedVesselName = useMemo(
    () => vessels.find((v) => String(v.id) === appliedVesselId)?.vesselName ?? null,
    [vessels, appliedVesselId],
  );

  const vesselOptions = useMemo(
    () => vessels.map((v) => ({ value: String(v.id), label: v.vesselName })),
    [vessels],
  );

  const handleSearch = () => {
    if (!vesselId) return;
    setAppliedVesselId(vesselId);
  };

  const certCategoryById = useMemo(() => {
    const map = new Map<number, LookupItem>();
    for (const c of certCategories) map.set(c.id, c);
    return map;
  }, [certCategories]);

  const certGroups = useMemo(() => {
    const map = new Map<string, CertCategoryGroup>();
    for (const cert of certCatalogs) {
      const categoryId = cert.categoryId ?? null;
      const category = categoryId != null ? certCategoryById.get(categoryId) : undefined;
      const key = categoryId != null ? `cat-${categoryId}` : 'uncategorized';
      const label = category?.name?.trim() || 'Uncategorized';
      const sortOrder = category?.sortOrder ?? (categoryId == null ? 999999 : 0);
      const existing = map.get(key);
      if (existing) {
        existing.certs.push(cert);
      } else {
        map.set(key, { key, label, sortOrder, certs: [cert] });
      }
    }
    return Array.from(map.values())
      .map((g) => ({
        ...g,
        certs: [...g.certs].sort((a, b) => {
          const ao = Number(a.sortOrder);
          const bo = Number(b.sortOrder);
          const aOrder = Number.isFinite(ao) ? ao : 999_999;
          const bOrder = Number.isFinite(bo) ? bo : 999_999;
          if (aOrder !== bOrder) return aOrder - bOrder;
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        }),
      }))
      .sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.label.localeCompare(b.label);
      });
  }, [certCatalogs, certCategoryById]);

  const requiredCount = enabledSet.size;
  const totalCells = certCatalogs.length * matrixNationalities.length;

  const cellKey = (certId: number, natId: number) => `${certId}:${natId}`;
  const columnAllKey = (groupKey: string, natId: number) => `all:${groupKey}:${natId}`;

  const matrixGridStyle = useMemo(
    () =>
      ({
        gridTemplateColumns: `${CERT_COL_PX}px repeat(${Math.max(matrixNationalities.length, 1)}, minmax(${NAT_COL_PX}px, 1fr))`,
        minWidth: `${CERT_COL_PX + Math.max(matrixNationalities.length, 1) * NAT_COL_PX}px`,
      }) as React.CSSProperties,
    [matrixNationalities.length],
  );

  const onToggle = async (certCatalogId: number, nationalityId: number, enabled: boolean) => {
    if (selectedRankId == null || !appliedVesselId) return;
    const key = cellKey(certCatalogId, nationalityId);
    setPendingCells((s) => new Set(s).add(key));
    try {
      await api.put('/crew/rank-requirements/cell', {
        rankId: selectedRankId,
        vesselId: Number(appliedVesselId),
        certCatalogId,
        nationalityId,
        enabled,
      });
      await mutate();
      showSuccess(enabled ? 'Requirement enabled' : 'Requirement removed');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e instanceof Error ? e.message : 'Update failed');
      showError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    } finally {
      setPendingCells((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  };

  const onToggleAllForNationality = async (
    groupKey: string,
    certs: CertCatalogLookup[],
    nationalityId: number,
    enabled: boolean,
  ) => {
    if (selectedRankId == null || !appliedVesselId || certs.length === 0) return;
    const targets = certs.filter((c) => enabledSet.has(cellKey(c.id, nationalityId)) !== enabled);
    if (targets.length === 0) return;

    const allKey = columnAllKey(groupKey, nationalityId);
    const targetKeys = targets.map((c) => cellKey(c.id, nationalityId));
    setPendingCells((s) => {
      const next = new Set(s);
      next.add(allKey);
      for (const k of targetKeys) next.add(k);
      return next;
    });

    try {
      await Promise.all(
        targets.map((c) =>
          api.put('/crew/rank-requirements/cell', {
            rankId: selectedRankId,
            vesselId: Number(appliedVesselId),
            certCatalogId: c.id,
            nationalityId,
            enabled,
          }),
        ),
      );
      await mutate();
      showSuccess(
        enabled
          ? `Enabled ${targets.length} requirement${targets.length === 1 ? '' : 's'}`
          : `Removed ${targets.length} requirement${targets.length === 1 ? '' : 's'}`,
      );
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e instanceof Error ? e.message : 'Update failed');
      showError(Array.isArray(msg) ? msg.join(', ') : String(msg));
      await mutate();
    } finally {
      setPendingCells((s) => {
        const next = new Set(s);
        next.delete(allKey);
        for (const k of targetKeys) next.delete(k);
        return next;
      });
    }
  };

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const noNationalities = !loadingNats && matrixNationalities.length === 0;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <CrewReportSearchHeader
        hideBack
        hint={{
          title: 'Document Requirements',
          body: 'Define which certificates are required for each rank and nationality on a selected vessel. Choose a vessel, search, pick a rank, then toggle cells on/off. Use the column header control in a category to switch every certificate for that nationality at once. Configure nationality columns in Application settings.',
        }}
        onSearch={handleSearch}
        searchDisabled={!vesselId}
        loading={isLoading || loadingNats}
        onRefresh={appliedVesselId ? () => mutate() : undefined}
        trailingActions={
          appliedVesselId ? (
            <div ref={printMenuRef} className="relative">
              <button
                type="button"
                title="Print preview"
                aria-haspopup="menu"
                aria-expanded={printMenuOpen}
                onClick={() => setPrintMenuOpen((v) => !v)}
                className={cn(
                  'inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
                  colors.border,
                  colors.text,
                  'hover:bg-black/5 dark:hover:bg-white/10',
                )}
              >
                <PrinterIcon className="h-4 w-4" />
                Print
                <ChevronDownIcon
                  className={cn('h-3.5 w-3.5 transition-transform', printMenuOpen && 'rotate-180')}
                />
              </button>
              {printMenuOpen ? (
                <div
                  role="menu"
                  className={cn(
                    'absolute right-0 z-40 mt-1.5 w-64 overflow-hidden rounded-lg border py-1 shadow-lg',
                    colors.surface,
                    colors.border,
                  )}
                >
                  {selectedRankId != null ? (
                    <Link
                      role="menuitem"
                      to={rankRequirementsPrintPath({
                        vesselId: appliedVesselId,
                        rankId: selectedRankId,
                      })}
                      onClick={() => setPrintMenuOpen(false)}
                      className={cn(
                        'block px-3 py-2 text-sm transition-colors',
                        colors.text,
                        'hover:bg-black/5 dark:hover:bg-white/10',
                      )}
                    >
                      By rank
                      <span className={cn('mt-0.5 block text-xs', colors.textMuted)}>
                        Certificates × nationalities
                      </span>
                    </Link>
                  ) : (
                    <div
                      className={cn('px-3 py-2 text-sm', colors.textMuted)}
                      title="Select a rank first"
                    >
                      By rank
                      <span className="mt-0.5 block text-xs">Select a rank first</span>
                    </div>
                  )}
                  <div
                    className={cn(
                      'border-t px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide',
                      colors.border,
                      colors.textMuted,
                    )}
                  >
                    By nationality
                  </div>
                  {matrixNationalities.length === 0 ? (
                    <div className={cn('px-3 py-2 text-sm', colors.textMuted)}>
                      No nationalities configured
                    </div>
                  ) : (
                    matrixNationalities.map((n) => (
                      <Link
                        key={n.id}
                        role="menuitem"
                        to={rankRequirementsByNationalityPrintPath({
                          vesselId: appliedVesselId,
                          nationalityId: n.id,
                        })}
                        onClick={() => setPrintMenuOpen(false)}
                        className={cn(
                          'block px-3 py-2 text-sm transition-colors',
                          colors.text,
                          'hover:bg-black/5 dark:hover:bg-white/10',
                        )}
                      >
                        {n.code || n.name}
                        {n.code && n.name ? (
                          <span className={cn('mt-0.5 block text-xs', colors.textMuted)}>
                            {n.name}
                          </span>
                        ) : null}
                      </Link>
                    ))
                  )}
                </div>
              ) : null}
            </div>
          ) : null
        }
      >
        <CrewReportFilterField label="Vessel" htmlFor="rr-vessel" width="lg">
          <Select
            name="vesselId"
            value={vesselId}
            onChange={(e) => setVesselId(String(e.target.value))}
            options={vesselOptions}
            placeholder="Select vessel…"
            searchPlaceholder="Search vessels…"
          />
        </CrewReportFilterField>
      </CrewReportSearchHeader>

      {noNationalities && (
        <div
          className={cn(
            'rounded-lg border px-4 py-3 text-sm',
            colors.border,
            isDark ? 'bg-amber-950/40 text-amber-100' : 'bg-amber-50 text-amber-900',
          )}
        >
          No requirement nationalities configured.{' '}
          <Link to="/admin/settings/application" className="font-medium underline">
            Choose 1–6 nationalities in Application settings
          </Link>{' '}
          first.
        </div>
      )}

      {!noNationalities && matrixNationalities.length > 6 && (
        <div
          className={cn(
            'rounded-lg border px-4 py-2.5 text-sm',
            colors.border,
            isDark ? 'bg-amber-950/30 text-amber-100/90' : 'bg-amber-50 text-amber-900',
          )}
        >
          {matrixNationalities.length} nationality columns are configured (recommended max 6). Scroll
          horizontally to see all columns, or trim the list in{' '}
          <Link to="/admin/settings/application" className="font-medium underline">
            Application settings
          </Link>
          .
        </div>
      )}

      <div
        className={cn(
          'flex min-h-0 flex-1 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <aside className={cn('flex w-[260px] shrink-0 flex-col border-r', colors.border)}>
          <div className={cn('border-b px-3 py-2.5', colors.border)}>
            <p className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>Ranks</p>
          </div>
          <div className={cn('border-b px-3 py-2', colors.border)}>
            <div className="relative">
              <SearchIcon
                className={cn(
                  'pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2',
                  colors.textMuted,
                )}
              />
              <input
                type="search"
                value={rankFilter}
                onChange={(e) => setRankFilter(e.target.value)}
                placeholder="Filter ranks…"
                className={cn(
                  'h-8 w-full rounded-md border pl-8 pr-3 text-sm',
                  colors.border,
                  colors.surface,
                  colors.text,
                  'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                )}
              />
            </div>
          </div>
          <div className="dashboard-scrollbar flex-1 overflow-y-auto py-1">
            {filteredRanks.map((rank) => {
              const active = selectedRankId === rank.id;
              return (
                <button
                  key={rank.id}
                  type="button"
                  onClick={() => setSelectedRankId(rank.id)}
                  className={cn(
                    'flex h-9 w-full items-center gap-2 px-3 text-left text-sm transition-colors',
                    active
                      ? 'bg-blue-600 text-white'
                      : cn(colors.text, colors.interactiveHover),
                  )}
                >
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {rank.abrv ? (
                      <>
                        <span className={active ? 'text-white/80' : colors.textMuted}>{rank.abrv}</span>
                        <span className="mx-1.5 opacity-40">·</span>
                        {rank.name}
                      </>
                    ) : (
                      rank.name
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="flex min-w-0 flex-1 flex-col">
          {!appliedVesselId ? (
            <div className={cn('flex flex-1 items-center justify-center text-sm', colors.textMuted)}>
              Select a vessel, then search.
            </div>
          ) : !selectedRank ? (
            <div className={cn('flex flex-1 items-center justify-center text-sm', colors.textMuted)}>
              Select a rank.
            </div>
          ) : (
            <>
              <div
                className={cn(
                  'flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3',
                  colors.border,
                  colors.headerBg,
                )}
              >
                <div className="min-w-0">
                  <h2 className={cn('truncate text-base font-semibold', colors.text)}>
                    {selectedRank.name}
                    {selectedRank.abrv ? (
                      <span className={cn('ml-2 text-sm font-medium', colors.textMuted)}>
                        {selectedRank.abrv}
                      </span>
                    ) : null}
                  </h2>
                  <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>
                    {appliedVesselName ?? 'Vessel'}
                    {matrixNationalities.length > 0
                      ? ` · ${matrixNationalities.map((n) => n.code || n.name).join(', ')}`
                      : ''}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <CountChip>
                    {requiredCount} required
                  </CountChip>
                  {totalCells > 0 ? (
                    <span className={cn('text-xs', colors.textMuted)}>
                      of {totalCells} cells · {certGroups.length} categories
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="dashboard-scrollbar min-h-0 flex-1 overflow-auto">
                {noNationalities ? (
                  <div className={cn('px-4 py-12 text-center text-sm', colors.textMuted)}>
                    Configure nationalities to edit the matrix.
                  </div>
                ) : isLoading || loadingNats ? (
                  <p className={cn('px-4 py-8 text-sm', colors.textMuted)}>Loading…</p>
                ) : certGroups.length === 0 ? (
                  <div className={cn('px-4 py-12 text-center text-sm', colors.textMuted)}>
                    No certificates in the catalog yet.
                  </div>
                ) : (
                  <div className="min-w-0">
                    <div
                      className={cn(
                        'sticky top-0 z-20 grid items-center border-b px-3 py-2.5 shadow-sm',
                        colors.headerBg,
                        colors.border,
                      )}
                      style={matrixGridStyle}
                    >
                      <div
                        className={cn(
                          'sticky left-0 z-[1] pr-3 text-xs font-semibold uppercase tracking-wide',
                          colors.headerBg,
                          colors.textMuted,
                        )}
                      >
                        Certificate
                      </div>
                      {matrixNationalities.map((n) => (
                        <div
                          key={n.id}
                          className="px-1 text-center"
                          title={n.name}
                        >
                          <div className={cn('text-xs font-bold uppercase tracking-wide', colors.text)}>
                            {n.code || n.name}
                          </div>
                          <div className={cn('mt-0.5 truncate text-[10px]', colors.textMuted)}>
                            {n.code && n.name !== n.code ? n.name : '\u00A0'}
                          </div>
                        </div>
                      ))}
                    </div>

                    {certGroups.map((group) => {
                      const open = !collapsedGroups.has(group.key);
                      const groupEnabled = group.certs.reduce(
                        (sum, c) =>
                          sum +
                          matrixNationalities.filter((n) => enabledSet.has(cellKey(c.id, n.id))).length,
                        0,
                      );
                      const groupTotal = group.certs.length * matrixNationalities.length;

                      return (
                        <div key={group.key} className={cn('border-b', colors.border)}>
                          <div
                            className={cn(
                              'sticky top-[3.25rem] z-10 grid items-center px-3 py-2',
                              isDark ? 'bg-slate-800/95' : 'bg-slate-50/95',
                              colors.border,
                            )}
                            style={matrixGridStyle}
                          >
                            <div
                              className={cn(
                                'sticky left-0 z-[1] flex min-w-0 items-center gap-2 pr-3',
                                isDark ? 'bg-slate-800/95' : 'bg-slate-50/95',
                              )}
                            >
                              <button
                                type="button"
                                onClick={() => toggleGroup(group.key)}
                                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                                aria-expanded={open}
                              >
                                <svg
                                  className={cn(
                                    'h-4 w-4 shrink-0 transition-transform',
                                    colors.textMuted,
                                    open && 'rotate-180',
                                  )}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                                <span className={cn('truncate text-sm font-semibold', colors.text)}>
                                  {group.label}
                                </span>
                                <span
                                  className={cn(
                                    'shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium tabular-nums',
                                    isDark ? 'bg-slate-700 text-slate-200' : 'bg-white text-slate-600 border',
                                    colors.border,
                                  )}
                                >
                                  {groupEnabled}/{groupTotal}
                                </span>
                              </button>
                            </div>

                            {matrixNationalities.map((n) => {
                              const enabledCount = group.certs.filter((c) =>
                                enabledSet.has(cellKey(c.id, n.id)),
                              ).length;
                              const allOn = group.certs.length > 0 && enabledCount === group.certs.length;
                              const someOn = enabledCount > 0 && !allOn;
                              const pending =
                                pendingCells.has(columnAllKey(group.key, n.id)) ||
                                group.certs.some((c) => pendingCells.has(cellKey(c.id, n.id)));
                              return (
                                <div key={n.id} className="flex justify-center px-1">
                                  <button
                                    type="button"
                                    disabled={pending || group.certs.length === 0}
                                    onClick={() =>
                                      onToggleAllForNationality(group.key, group.certs, n.id, !allOn)
                                    }
                                    title={
                                      allOn
                                        ? `Turn off all ${n.code || n.name} in ${group.label}`
                                        : `Require all ${n.code || n.name} in ${group.label}`
                                    }
                                    className={cn(
                                      'inline-flex h-7 min-w-[3.25rem] items-center justify-center rounded-md border px-2 text-[11px] font-semibold transition-colors disabled:opacity-50',
                                      allOn
                                        ? cn(colors.primary, colors.primaryText, 'border-transparent')
                                        : someOn
                                          ? isDark
                                            ? 'border-blue-400/50 bg-blue-950/40 text-blue-200'
                                            : 'border-blue-200 bg-blue-50 text-blue-800'
                                          : cn(colors.border, colors.textMuted, colors.interactiveHover),
                                    )}
                                  >
                                    {allOn ? 'All' : someOn ? `${enabledCount}` : 'All'}
                                  </button>
                                </div>
                              );
                            })}
                          </div>

                          {open
                            ? group.certs.map((cert) => (
                                <div
                                  key={cert.id}
                                  className={cn(
                                    'grid items-center border-t px-3 py-1.5 transition-colors',
                                    colors.border,
                                    colors.interactiveHover,
                                  )}
                                  style={matrixGridStyle}
                                >
                                  <div
                                    className={cn(
                                      'sticky left-0 z-[1] min-w-0 truncate pr-3 text-sm',
                                      colors.surface,
                                      colors.text,
                                    )}
                                    title={cert.name}
                                  >
                                    {cert.name}
                                  </div>
                                  {matrixNationalities.map((n) => {
                                    const key = cellKey(cert.id, n.id);
                                    const enabled = enabledSet.has(key);
                                    const pending = pendingCells.has(key);
                                    return (
                                      <div key={n.id} className="flex justify-center px-1">
                                        <RequirementCell
                                          enabled={enabled}
                                          pending={pending}
                                          label={`${cert.name} · ${n.name}`}
                                          onToggle={() => onToggle(cert.id, n.id, !enabled)}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              ))
                            : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

const RequirementCell: React.FC<{
  enabled: boolean;
  pending: boolean;
  label: string;
  onToggle: () => void;
}> = ({ enabled, pending, label, onToggle }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      disabled={pending}
      onClick={onToggle}
      aria-pressed={enabled}
      aria-label={label}
      title={enabled ? 'Required — click to remove' : 'Not required — click to require'}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-all disabled:opacity-50',
        enabled
          ? cn(colors.primary, colors.primaryText, 'border-transparent shadow-sm')
          : cn(
              colors.border,
              isDark ? 'bg-slate-900/40 hover:bg-slate-800' : 'bg-white hover:bg-slate-50',
              colors.textMuted,
            ),
      )}
    >
      {enabled ? <CheckIcon className="h-4 w-4" /> : <span className="h-1.5 w-1.5 rounded-full bg-current opacity-30" />}
    </button>
  );
};

export default RankRequirementsPage;
